export const CONSENT_FIELD_TYPES = [
  { value: "text", label: "Free text" },
  { value: "select", label: "Dropdown (searchable)" },
];

export const CONSENT_FIELD_DATA_PREFIX = "field.";

export const CONSENT_FIELD_LIMITS = {
  fieldsPerTemplate: 30,
  keyMax: 60,
  labelMax: 120,
  optionsMax: 50,
  optionMax: 120,
};

export const DEFAULT_CONSENT_FIELD_SOURCES = [
  { key: "specialityTreatment", label: "Speciality Treatments master" },
  { key: "diagnosis", label: "Diagnosis master" },
  { key: "drug", label: "Drug master" },
  { key: "investigation", label: "Investigation (Test) master" },
  { key: "dayCareProcedure", label: "Day Care Procedure master" },
  { key: "laboratory", label: "Laboratory master" },
  // Not a master: the doctor app draws its tooth chart and stores "16, 26".
  { key: "teeth", label: "Teeth (tooth chart)" },
];

const text = (value) => String(value ?? "").trim();

const asArray = (value) => (Array.isArray(value) ? value : []);

export const consentFieldDataKey = (key) => `${CONSENT_FIELD_DATA_PREFIX}${key}`;

/** A field key reads like a placeholder segment: `{{field.toothNumber}}`. */
export const consentFieldKeyOf = (label, taken = []) => {
  const words = text(label)
    .replace(/[^A-Za-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean);
  const base =
    words
      .map((word, index) =>
        index === 0
          ? word.toLowerCase()
          : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
      .join("")
      .slice(0, CONSENT_FIELD_LIMITS.keyMax) || "field";

  const used = new Set(asArray(taken).map((item) => String(item)));
  if (!used.has(base)) return base;
  for (let suffix = 2; suffix < 1000; suffix += 1) {
    const candidate = `${base}${suffix}`;
    if (!used.has(candidate)) return candidate;
  }
  return `${base}${Date.now()}`;
};

const isKey = (value) => /^[A-Za-z0-9_]{1,60}$/.test(value);

export const parseOptionsText = (value) =>
  text(value)
    .split(/[\n,]/)
    .map((option) => option.trim().slice(0, CONSENT_FIELD_LIMITS.optionMax))
    .filter(Boolean)
    .filter((option, index, all) => all.indexOf(option) === index)
    .slice(0, CONSENT_FIELD_LIMITS.optionsMax);

export const optionsText = (options) => asArray(options).join("\n");

export const normalizeConsentFields = (input, { sources } = {}) => {
  const allowed = Array.isArray(sources) ? new Set(sources.map(String)) : null;
  const out = [];
  const taken = new Set();

  for (const raw of asArray(input).slice(0, CONSENT_FIELD_LIMITS.fieldsPerTemplate)) {
    if (!raw || typeof raw !== "object") continue;

    const label = text(raw.label).slice(0, CONSENT_FIELD_LIMITS.labelMax);
    if (!label) continue;

    let key = text(raw.key);
    if (!isKey(key)) key = consentFieldKeyOf(label, [...taken]);
    if (taken.has(key)) continue;
    taken.add(key);

    const type = raw.type === "select" ? "select" : "text";
    const wanted = text(raw.source);
    const source =
      type === "select" && wanted && (!allowed || allowed.has(wanted)) ? wanted : "";
    const options =
      type === "select" && !source ? parseOptionsText(asArray(raw.options).join("\n")) : [];

    out.push({ key, label, type, source, options });
  }

  return out;
};

/** The fields a design declares, as the doctor app will ask for them. */
export const consentFieldsOf = (design) =>
  normalizeConsentFields(design?.settings?.consentFields);

export const draftConsentFieldsOf = (design) =>
  asArray(design?.settings?.consentFields).filter(
    (field) => field && typeof field === "object"
  );

/** The design with these fields declared; everything else untouched. */
export const withConsentFields = (design, fields) => ({
  ...(design && typeof design === "object" ? design : {}),
  pages: asArray(design?.pages),
  settings: {
    ...(design?.settings && typeof design.settings === "object" ? design.settings : {}),
    consentFields: asArray(fields)
      .filter((field) => field && typeof field === "object")
      .slice(0, CONSENT_FIELD_LIMITS.fieldsPerTemplate),
  },
});

/** The starter fields the catalogue suggests for a consent format. */
export const presetFieldsFor = (type, presets) =>
  normalizeConsentFields(presets?.[text(type).toUpperCase()] || presets?.[text(type)] || []);

/** The masters the builder offers: the server's list, else this build's own. */
export const offeredConsentFieldSources = (served) =>
  (Array.isArray(served) && served.length ? served : DEFAULT_CONSENT_FIELD_SOURCES)
    .filter((item) => item && text(item.key))
    .map((item) => ({ key: text(item.key), label: text(item.label) || text(item.key) }));

export const addConsentField = (fields, { label = "", type = "text" } = {}) => {
  const current = asArray(fields);
  if (current.length >= CONSENT_FIELD_LIMITS.fieldsPerTemplate) return current;
  return [
    ...current,
    {
      key: consentFieldKeyOf(label || "New field", current.map((item) => item.key)),
      label,
      type: type === "select" ? "select" : "text",
      source: "",
      options: [],
    },
  ];
};

export const updateConsentField = (fields, index, patch = {}) =>
  asArray(fields).map((field, at) => {
    if (at !== index) return field;

    const next = { ...field, ...patch };
    if ("label" in patch) {
      next.key = consentFieldKeyOf(
        patch.label,
        asArray(fields)
          .filter((item, other) => other !== index)
          .map((item) => item.key)
      );
    }
    if (next.type !== "select") {
      next.type = "text";
      next.source = "";
      next.options = [];
    }
    if (next.source) next.options = [];
    return next;
  });

export const removeConsentField = (fields, index) =>
  asArray(fields).filter((field, at) => at !== index);

export const moveConsentField = (fields, index, delta) => {
  const list = [...asArray(fields)];
  const target = index + delta;
  if (index < 0 || index >= list.length || target < 0 || target >= list.length) return list;
  const [moved] = list.splice(index, 1);
  list.splice(target, 0, moved);
  return list;
};

export const consentFieldElementId = (key) => `admin-preview-consent-field-${key}`;

export const consentFieldPreviewElements = (fields) =>
  asArray(fields).map((field) => ({
    id: consentFieldElementId(field.key),
    type: "dynamicField",
    field: consentFieldDataKey(field.key),
    label: { en: field.label, hi: field.label },
    content: { en: "", hi: "" },
    props: { inline: true, fallback: "" },
    style: {},
    layout: {},
  }));
